import { AnimatePresence, motion } from 'framer-motion';
import {
    CheckCircle2,
    LayoutDashboard,
    ListFilter,
    LucideIcon,
    PlusCircle,
    Receipt
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Types
interface StoryStepData {
    id: string;
    titleKey: string;
    descriptionKey: string;
    image: string;
    icon: LucideIcon;
    color: string;
    hoverColor: string;
    bg: string;
}

// Images
// Note: WebP images from public folder
const ausgabeImg = '/ausgabe.webp';
const ausgabeBelegImg = '/ausgabebeleg.webp';
const ausgabenListeImg = '/ausgabenliste.webp';
const ausgabenListeDetailImg = '/ausgabenliste-detail.webp';
const heroScreen = '/hero_screenshot_main.png';
const jahresuebersichtImg = '/jahresübersicht.webp';
const monatsabschlussImg = '/monatsabschluß.webp';

// Brand Colors: #237CCA (Blau) and #10B981 (Grün)
// Pattern: Spalte 1 BLAU → Spalte 2 GRÜN → Spalte 3 BLAU → Spalte 4 GRÜN

// 2. Merged Dynamic Step (Ausgaben erfassen & Details) - GRÜN
const mergedSteps: StoryStepData[] = [
    {
        id: 'add',
        titleKey: 'howItWorks.steps.add.add.title',
        descriptionKey: 'howItWorks.steps.add.add.description',
        image: ausgabeImg,
        icon: PlusCircle,
        color: 'text-[#10B981]',
        hoverColor: 'group-hover:text-[#10B981]',
        bg: 'bg-emerald-50',
    },
    {
        id: 'details',
        titleKey: 'howItWorks.steps.add.details.title',
        descriptionKey: 'howItWorks.steps.add.details.description',
        image: ausgabeBelegImg,
        icon: Receipt,
        color: 'text-[#10B981]',
        hoverColor: 'group-hover:text-[#10B981]',
        bg: 'bg-emerald-50',
    }
];

// 3. Verify Dynamic Steps (Prüfen & Klären) - BLAU
const verifySteps: StoryStepData[] = [
    {
        id: 'verify-list',
        titleKey: 'howItWorks.steps.verify.list.title',
        descriptionKey: 'howItWorks.steps.verify.list.description',
        image: ausgabenListeImg,
        icon: ListFilter,
        color: 'text-[#237CCA]',
        hoverColor: 'group-hover:text-[#237CCA]',
        bg: 'bg-blue-50',
    },
    {
        id: 'verify-detail',
        titleKey: 'howItWorks.steps.verify.detail.title',
        descriptionKey: 'howItWorks.steps.verify.detail.description',
        image: ausgabenListeDetailImg,
        icon: ListFilter,
        color: 'text-[#237CCA]',
        hoverColor: 'group-hover:text-[#237CCA]',
        bg: 'bg-blue-50',
    }
];

// 4. Close Dynamic Steps (Monatsabschluss) - GRÜN
const closeSteps: StoryStepData[] = [
    {
        id: 'close-overview',
        titleKey: 'howItWorks.steps.close.overview.title',
        descriptionKey: 'howItWorks.steps.close.overview.description',
        image: jahresuebersichtImg,
        icon: CheckCircle2,
        color: 'text-[#10B981]',
        hoverColor: 'group-hover:text-[#10B981]',
        bg: 'bg-emerald-50',
    },
    {
        id: 'close-detail',
        titleKey: 'howItWorks.steps.close.detail.title',
        descriptionKey: 'howItWorks.steps.close.detail.description',
        image: monatsabschlussImg,
        icon: CheckCircle2,
        color: 'text-[#10B981]',
        hoverColor: 'group-hover:text-[#10B981]',
        bg: 'bg-emerald-50',
    }
];

// 1. Hero Dynamic Steps (Alles im Blick) - BLAU
const heroSteps: StoryStepData[] = [
    {
        id: 'overview',
        titleKey: 'howItWorks.steps.hero.overview.title',
        descriptionKey: 'howItWorks.steps.hero.overview.description',
        image: heroScreen,
        icon: LayoutDashboard,
        color: 'text-[#237CCA]',
        hoverColor: 'group-hover:text-[#237CCA]',
        bg: 'bg-blue-50',
    },
    {
        id: 'dashboard-kind',
        titleKey: 'howItWorks.steps.hero.dashboardKind.title',
        descriptionKey: 'howItWorks.steps.hero.dashboardKind.description',
        image: '/dashbord-kind.webp',
        icon: LayoutDashboard,
        color: 'text-[#237CCA]',
        hoverColor: 'group-hover:text-[#237CCA]',
        bg: 'bg-blue-50',
    }
];

export function UserStorySection() {
    const { t } = useTranslation();

    // Central controller for sequential animation on desktop
    // activeColumn: 0-3 = which column should animate next, -1 = pause between cycles
    const [activeColumn, setActiveColumn] = useState(0);
    const [isDesktop, setIsDesktop] = useState(false);
    const [isPaused, setIsPaused] = useState(false);

    // Detect desktop (lg breakpoint = 1024px, where 4 columns are shown)
    useEffect(() => {
        const checkDesktop = () => setIsDesktop(window.innerWidth >= 1024);
        checkDesktop();
        window.addEventListener('resize', checkDesktop);
        return () => window.removeEventListener('resize', checkDesktop);
    }, []);

    // Sequential animation controller for desktop
    // Flow: Column 0 → 2s pause → Column 1 → 2s pause → Column 2 → 2s pause → Column 3 → 4s pause → restart
    useEffect(() => {
        if (!isDesktop) return;
        if (isPaused) return;

        const timer = setTimeout(() => {
            setActiveColumn((prev) => {
                if (prev >= 3) return 0; // After column 3, restart at 0
                return prev + 1;
            });
        }, activeColumn === 3 ? 5000 : 3000); // Longer pause after last column

        return () => clearTimeout(timer);
    }, [activeColumn, isDesktop, isPaused]);

    return (
        <section id="how-it-works" className="py-24 bg-white relative overflow-hidden">
             {/* Simple Grid Background Pattern */}
             <div className="absolute inset-0 z-0 pointer-events-none opacity-[0.03]"
                  style={{
                      backgroundImage: `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
                      backgroundSize: '40px 40px'
                  }}
             />

            <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1400px] relative z-10">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6 font-nunito">
                        {t('howItWorks.title')}
                    </h2>
                    <p className="text-xl text-slate-600 max-w-2xl mx-auto font-nunito">
                        {t('howItWorks.description')}
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 items-start">
                    {/* Desktop: Sequential animation controlled by activeColumn */}
                    {/* Mobile: Each column animates independently */}
                    <DynamicStoryStep
                        steps={heroSteps}
                        columnIndex={0}
                        activeColumn={activeColumn}
                        isDesktop={isDesktop}
                        onHoverStart={() => setIsPaused(true)}
                        onHoverEnd={() => setIsPaused(false)}
                    />
                    <DynamicStoryStep
                        steps={mergedSteps}
                        columnIndex={1}
                        activeColumn={activeColumn}
                        isDesktop={isDesktop}
                        onHoverStart={() => setIsPaused(true)}
                        onHoverEnd={() => setIsPaused(false)}
                    />
                    <DynamicStoryStep
                        steps={verifySteps}
                        columnIndex={2}
                        activeColumn={activeColumn}
                        isDesktop={isDesktop}
                        onHoverStart={() => setIsPaused(true)}
                        onHoverEnd={() => setIsPaused(false)}
                    />
                    <DynamicStoryStep
                        steps={closeSteps}
                        columnIndex={3}
                        activeColumn={activeColumn}
                        isDesktop={isDesktop}
                        onHoverStart={() => setIsPaused(true)}
                        onHoverEnd={() => setIsPaused(false)}
                    />
                </div>
            </div>
        </section>
    );
}



interface DynamicStoryStepProps {
    steps: StoryStepData[];
    columnIndex: number;
    activeColumn: number;
    isDesktop: boolean;
    onHoverStart?: () => void;
    onHoverEnd?: () => void;
}

function DynamicStoryStep({ steps, columnIndex, activeColumn, isDesktop, onHoverStart, onHoverEnd }: DynamicStoryStepProps) {
    const { t } = useTranslation();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [hasAnimated, setHasAnimated] = useState(false);
    const [mobilePaused, setMobilePaused] = useState(false);
    const mobilePauseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Desktop: Switch when this column becomes active (only once per cycle)
    useEffect(() => {
        if (isDesktop && activeColumn === columnIndex && !hasAnimated) {
            setCurrentIndex((prev) => (prev + 1) % steps.length);
            setHasAnimated(true);
        }
        // Reset flag when activeColumn moves away
        if (activeColumn !== columnIndex) {
            setHasAnimated(false);
        }
    }, [activeColumn, columnIndex, isDesktop, steps.length, hasAnimated]);

    // Mobile: Independent timer with staggered start
    useEffect(() => {
        if (isDesktop) return;
        if (mobilePaused) return;

        const mobileDelay = columnIndex * 2000;
        const mobileInterval = 6000;

        let intervalId: ReturnType<typeof setInterval>;

        const startTimeout = setTimeout(() => {
            intervalId = setInterval(() => {
                setCurrentIndex((prev) => (prev + 1) % steps.length);
            }, mobileInterval);
        }, mobileDelay);

        return () => {
            clearTimeout(startTimeout);
            if (intervalId) clearInterval(intervalId);
        };
    }, [columnIndex, isDesktop, steps.length, mobilePaused]);

    // Cleanup mobile pause timer on unmount
    useEffect(() => {
        return () => {
            if (mobilePauseTimerRef.current) {
                clearTimeout(mobilePauseTimerRef.current);
            }
        };
    }, []);

    const handleMobileTap = () => {
        if (isDesktop) return;
        setCurrentIndex((prev) => (prev + 1) % steps.length);
        setMobilePaused(true);
        if (mobilePauseTimerRef.current) {
            clearTimeout(mobilePauseTimerRef.current);
        }
        mobilePauseTimerRef.current = setTimeout(() => {
            setMobilePaused(false);
        }, 5000);
    };

    const currentStep = steps[currentIndex] || steps[0];

    return (
        <div
            className="flex flex-col items-center text-center space-y-6 group cursor-default"
            onMouseEnter={() => isDesktop && onHoverStart?.()}
            onMouseLeave={() => isDesktop && onHoverEnd?.()}
        >
            {/* Image Area - Grid Stack for Carousel */}
            <div
                className="relative w-full max-w-[200px] lg:max-w-full aspect-[9/19.5] grid place-items-end mb-4 cursor-pointer"
                onClick={() => {
                    if (isDesktop) {
                        setCurrentIndex((prev) => (prev + 1) % steps.length);
                    } else {
                        handleMobileTap();
                    }
                }}
            >
                 {/* Decoration blob behind - Shared/Underlay */}
                 <div
                    className={`col-start-1 row-start-1 w-full h-full transform ${currentStep.bg} rounded-[2rem] blur-xl -z-10 opacity-40 group-hover:opacity-70 transition-all duration-700`}
                />

                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentStep.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.7, ease: "easeInOut" }}
                        className="col-start-1 row-start-1 w-full h-full"
                    >
                        <img
                            src={currentStep.image}
                            alt={t(currentStep.titleKey)}
                            className="w-full h-auto drop-shadow-xl rounded-2xl"
                        />
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Progress Dots - Shows which screenshot is active */}
            {steps.length > 1 && (
                <div className="flex items-center justify-center gap-2">
                    {steps.map((step, index) => {
                        // Extract hex color from class like "text-[#237CCA]"
                        const hexMatch = currentStep.color.match(/#[A-Fa-f0-9]{6}/);
                        const activeColor = hexMatch ? hexMatch[0] : '#237CCA';

                        return (
                            <button
                                key={step.id}
                                onClick={() => {
                                    setCurrentIndex(index);
                                    if (!isDesktop) {
                                        setMobilePaused(true);
                                        if (mobilePauseTimerRef.current) {
                                            clearTimeout(mobilePauseTimerRef.current);
                                        }
                                        mobilePauseTimerRef.current = setTimeout(() => {
                                            setMobilePaused(false);
                                        }, 5000);
                                    }
                                }}
                                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                    index === currentIndex ? 'scale-125' : 'bg-slate-300 hover:bg-slate-400'
                                }`}
                                style={index === currentIndex ? { backgroundColor: activeColor } : undefined}
                                aria-label={t('howItWorks.showStep', { step: t(step.titleKey) })}
                            />
                        );
                    })}
                </div>
            )}

            {/* Content Area - Grid Stack for Carousel */}
            <div className="w-full grid place-items-center px-2">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentStep.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                        className="col-start-1 row-start-1 w-full flex flex-col items-center space-y-3"
                    >
                         <h3 className={`text-xl font-bold text-slate-900 font-nunito leading-tight transition-all duration-300 ${currentStep.hoverColor} group-hover:-translate-y-1`}>
                            {t(currentStep.titleKey)}
                        </h3>

                        <p className="text-sm text-slate-600 leading-relaxed font-nunito">
                            {t(currentStep.descriptionKey)}
                        </p>
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
}
